import { FC, useCallback } from 'react';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import QrCode from 'dashboard/components/QrCode';
import FormMfaCode, { FormMfaCodeData } from 'dashboard/forms/FormMfaCode';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';

const Message = styled.p`
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const QrCodeContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const CODE_LENGTH = 6;

interface ConfirmMfaTotpModalProps {
  provisioningUri: string;
  onClose: () => void;
  onSubmit: (code: string) => Promise<void>;
}

const ConfirmMfaTotpModal: FC<ConfirmMfaTotpModalProps> = ({
  provisioningUri,
  onClose,
  onSubmit,
}) => {
  const handleSubmit = useCallback(
    async ({ code }: FormMfaCodeData) => {
      await onSubmit(code);
      onClose();
    },
    [onSubmit],
  );

  const qrTitle = 'Scan this QR code with your chosen authenticator app.';

  return (
    <ModalContainer>
      <ModalHeader onClose={onClose}>Confirm TOTP 2-factor authentication</ModalHeader>

      <ModalBody>
        <Message>
          {qrTitle} We recommend using an application such as{' '}
          <a href='https://authy.com/' target='_blank' rel='noreferrer'>
            Authy
          </a>
          ,{' '}
          <a href='https://1password.com/' target='_blank' rel='noreferrer'>
            1Password
          </a>
          , or{' '}
          <a href='https://lastpass.com/auth/' target='_blank' rel='noreferrer'>
            LastPass Authenticator
          </a>
          .
        </Message>

        <QrCodeContainer data-test='confirm-mfa-totp-modal__qr-code'>
          <QrCode value={provisioningUri} accessibleTitle={qrTitle} />
        </QrCodeContainer>

        <Message>Next, enter the verification code generated by your authenticator app.</Message>

        <FormMfaCode codeLength={CODE_LENGTH} onCancel={onClose} onSubmit={handleSubmit} />
      </ModalBody>
    </ModalContainer>
  );
};

export default ConfirmMfaTotpModal;
